import { useQuery } from '@tanstack/react-query';
import { Link, useParams } from '@tanstack/react-router';
import { BarChart3, Download } from 'lucide-react';
import { RecordLink } from '../components/fields.js';
import {
  buttonClass,
  Card,
  EmptyState,
  ErrorBanner,
  PageHeader,
  Spinner,
  Table,
  TD,
  TH,
} from '../components/ui.js';
import {
  countryName,
  formatDate,
  formatDateTime,
  formatMoney,
  formatNumber,
} from '../lib/format.js';
import { findEntity, useAppMeta } from '../lib/meta.js';
import { errorMessage, trpc, type RouterOutputs } from '../lib/trpc.js';

export type ReportResult = RouterOutputs['reports']['run'];
export type ReportColumn = ReportResult['columns'][number];
export type Row = ReportResult['rows'][number];

export const NUMERIC = new Set(['integer', 'decimal', 'money', 'percent']);

/** Saved reports this person can run. Reports are configuration, so a client's own appear here too. */
export function ReportIndex() {
  const meta = useAppMeta();
  return (
    <>
      <PageHeader
        title="Reports"
        subtitle="Saved reports you can run. Each one respects what you are allowed to see."
      />
      {meta.reports.length === 0 ? (
        <EmptyState title="No reports yet">
          An administrator can add reports in configuration.
        </EmptyState>
      ) : (
        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
          {meta.reports.map((report) => (
            <Link
              key={report.key}
              to="/reports/$key"
              params={{ key: report.key }}
              className="group rounded-lg border border-slate-200 bg-surface p-4 shadow-sm hover:border-brand"
            >
              <div className="flex items-center gap-2 text-sm font-semibold text-slate-900 group-hover:text-brand-strong">
                <BarChart3 className="size-4 text-slate-400" aria-hidden /> {report.label}
              </div>
              {report.description && (
                <p className="mt-1 text-sm text-slate-600">{report.description}</p>
              )}
              <p className="mt-2 text-xs text-slate-400">
                {findEntity(meta, report.entity)?.plural ?? report.entity}
              </p>
            </Link>
          ))}
        </div>
      )}
    </>
  );
}

export function ReportScreen() {
  const { key } = useParams({ strict: false }) as { key: string };
  const report = useQuery(trpc.reports.run.queryOptions({ key }));
  if (report.isLoading) return <Spinner />;
  if (report.error || !report.data)
    return (
      <ErrorBanner title="Could not run this report">{errorMessage(report.error)}</ErrorBanner>
    );
  const result = report.data;
  return (
    <>
      <PageHeader
        eyebrow={
          <Link to="/reports" className="hover:underline">
            Reports
          </Link>
        }
        title={result.label}
        subtitle={result.description ?? undefined}
        actions={
          <a
            href={`/downloads/reports/${encodeURIComponent(key)}`}
            className={buttonClass('secondary')}
            download
          >
            <Download className="size-4" aria-hidden /> Download CSV
          </a>
        }
      />
      {result.warnings.map((w) => (
        <ErrorBanner key={w} title="Read with care">
          {w}
        </ErrorBanner>
      ))}
      <div className="space-y-5">
        {result.chart && result.rows.length > 0 && (
          <Card title="Chart">
            <BarChart result={result} x={result.chart.x} y={result.chart.y} />
          </Card>
        )}
        <Card padded={false}>
          {result.rows.length === 0 ? (
            <EmptyState title="Nothing matches this report yet" />
          ) : (
            <Table stack>
              <thead>
                <tr>
                  {result.columns.map((c) => (
                    <TH key={c.key} align={NUMERIC.has(c.type) ? 'right' : 'left'}>
                      {heading(c)}
                    </TH>
                  ))}
                </tr>
              </thead>
              <tbody>
                {result.rows.map((row, i) => (
                  <tr key={i} className="hover:bg-slate-50">
                    {result.columns.map((c, j) => (
                      <TD
                        key={c.key}
                        align={NUMERIC.has(c.type) ? 'right' : 'left'}
                        label={heading(c)}
                        primary={j === 0}
                      >
                        <ReportCell column={c} row={row} titles={result.titles} />
                      </TD>
                    ))}
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </Card>
        <p className="text-xs text-slate-500">
          {result.truncated ? `Showing the first ${result.rows.length} rows; more matched. ` : ''}
          Run {formatDateTime(result.generated_at)}.
        </p>
      </div>
    </>
  );
}

/** A column's heading: its name, and the currency of an amount. */
function heading(column: ReportColumn): string {
  return `${column.label}${column.type === 'money' && column.currency ? ` (${column.currency})` : ''}`;
}

export function cellText(column: ReportColumn, row: Row, titles: Record<string, string>): string {
  const value = row[column.key];
  if (value === null || value === undefined || value === '') return '';
  if (column.reference) return titles[String(value)] ?? String(value);
  const option = column.options?.find((o) => o.value === value);
  if (option) return option.label;
  switch (column.type) {
    case 'money':
      // One currency for the whole column is named in its header; otherwise each row carries its own.
      return formatMoney(
        value,
        column.currency ? null : typeof row['$currency'] === 'string' ? row['$currency'] : null,
      );
    case 'integer':
    case 'decimal':
      return formatNumber(value);
    case 'percent':
      return `${formatNumber(value)}%`;
    case 'date':
      return formatDate(value);
    case 'datetime':
      return formatDateTime(value);
    case 'boolean':
      return value ? 'Yes' : 'No';
    case 'country':
      return countryName(value);
    default:
      return String(value);
  }
}

export function ReportCell({
  column,
  row,
  titles,
}: {
  column: ReportColumn;
  row: Row;
  titles: Record<string, string>;
}) {
  const text = cellText(column, row, titles);
  const value = row[column.key];
  if (column.reference && column.reference !== '*' && typeof value === 'string' && text) {
    return (
      <RecordLink entity={column.reference} id={value}>
        {text}
      </RecordLink>
    );
  }
  return <>{text}</>;
}

/** A plain bar chart: one bar per row, sized against the largest value. Enough to see a shape; the table has the numbers. */
export function BarChart({ result, x, y }: { result: ReportResult; x: string; y: string }) {
  const xColumn = result.columns.find((c) => c.key === x);
  const yColumn = result.columns.find((c) => c.key === y);
  if (!xColumn || !yColumn) return null;
  const bars = result.rows.map((row) => ({
    label: cellText(xColumn, row, result.titles) || '(none)',
    value: Number(row[y] ?? 0),
    text: cellText(yColumn, row, result.titles),
  }));
  const max = Math.max(...bars.map((b) => b.value), 0);
  return (
    <figure>
      <figcaption className="mb-3 text-xs text-slate-500">
        {yColumn.label} by {xColumn.label.toLowerCase()}
      </figcaption>
      <ul className="space-y-2">
        {bars.map((bar, i) => (
          <li
            key={i}
            className="grid grid-cols-[minmax(0,12rem)_1fr_auto] items-center gap-3 text-sm"
          >
            <span className="truncate text-slate-700" title={bar.label}>
              {bar.label}
            </span>
            <span className="h-3 rounded-full bg-slate-100">
              <span
                className="block h-3 rounded-full bg-brand"
                style={{ width: `${max > 0 ? Math.max((bar.value / max) * 100, 1) : 0}%` }}
              />
            </span>
            <span className="text-right text-slate-600 tabular-nums">{bar.text}</span>
          </li>
        ))}
      </ul>
    </figure>
  );
}
